import React from 'react';
import { CheckIcon, KeyRoundIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, offerChecklist, outcomes, painPoints } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/82a7a7a2-0b10-4537-9104-5b71c7e0263a.jpg';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Welcome',
  description: 'Answer every guest question before it is asked.'
}, {
  label: 'Protect',
  description: 'House rules and safety that hold up under pressure.'
}, {
  label: 'Impress',
  description: 'Local recommendations that earn written reviews.'
}, {
  label: 'Automate',
  description: 'Self check-in and check-out that run without you.'
}, {
  label: 'Maintain',
  description: 'One written standard for every single turnover.'
}, {
  label: 'Resolve',
  description: 'Emergency scripts so guests solve it first.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="35ryrh5o357sa">

      {/* Top banner */}
      <header data-id="element-11" data-mp="30ugixgdzcukt">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="30rua7dfcs910">
          <span aria-hidden="true" data-id="element-13" data-mp="3tiklht198rja">🔑 </span>Digital download — Instant access after purchase
        </div>
        
      </header>

      <main id="top" data-id="element-14" data-mp="3oj82jjdi84lz">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16 lg:py-20" data-id="element-15" data-mp="3ohesdu3on298">
          <div className="mx-auto max-w-4xl" data-id="element-16" data-mp="3jmhmux5m3b13">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-17" data-mp="3fjco95onjthq">
              Stop Answering the Same Guest Questions.{' '}
              <span className="text-brand-gold" data-id="element-18" data-mp="243nzq9vr2met">Let Your Property Run Itself.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl" data-id="element-19" data-mp="25ktle7q0fh6g">
              <strong className="font-black text-white" data-id="element-20" data-mp="2ajwkzcn80v71">The Hands-Off Hosting System</strong> gives you the welcome guides, house rules, check-in flows, and cleaning standards that turn constant messaging into a self-running stay.{' '}
              <strong className="font-black text-white" data-id="element-21" data-mp="20kp6un8nwwiv">Now with the full 6×1 system!</strong>
            </p>
            <img src={EBOOK_COVER_URL} alt="The complete Hands-Off Hosting System — six volumes plus 5 bonuses included" className="mx-auto mt-6 w-full max-w-2xl bg-transparent object-contain" data-id="element-22" data-mp="1mtk2j7ucng27" />
            <div className="mt-7" data-id="element-23" data-mp="1cxh0e1zhzhf9">
              <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" data-id="element-24" data-mp="1cu1lp09qkaed" />
              
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#EAE2D0] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-25" data-mp="13wr8h9dcyb6t">
          <span aria-hidden="true" data-id="element-26" data-mp="17yvzs1gc2vqa">🏡 </span>Built for short-term rental hosts who are done being on call 24/7
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-27" data-mp="kaplqlobooxb">
          <div className="mx-auto max-w-5xl" data-id="element-28" data-mp="y9pcpzzb5fwn">
            <div className="mx-auto max-w-2xl text-center" data-id="element-29" data-mp="y7cl0v3qzr6e">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-30" data-mp="t9b2qce5bvvz">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-31" data-mp="185a8vhhp3lyh">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-32" data-mp="1h21g8q1p6n8d">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-33" data-mp="1d0pmc8bsjfud">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-34" data-mp="1lzt67eis7z9x">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-35" data-mp="261nvvonoj6x2">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-36" data-mp="2a16j4r2z3u32">
              "You do not have a hosting problem. You have a missing operating system for your property."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-37" data-mp="1n439qpcaumg5">
              Most hosts answer the same twenty questions for every booking, explain check-in from scratch each time, and rely on memory for turnovers. That is not hospitality — it is unpaid admin. Documented systems are what separate a hands-off property from a second job.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-38" data-mp="3cgouy380457v">
          <div className="mx-auto max-w-3xl text-center" data-id="element-39" data-mp="10tle8wz3nkip">
            <KeyRoundIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-40" data-mp="10t37h6et9nwj" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-41" data-mp="vvjvye9hzp8a">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-42" data-mp="r1yv8egish5e">
              You don&apos;t need to work harder. You need a documented system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-43" data-mp="i1aj5paosqv5">
              <p data-id="element-44" data-mp="d2z512dczu74">
                The Hands-Off Hosting System is not another list of hosting tips. It is a complete 6-volume operating manual for your property: the guides guests read, the rules that protect you, and the standards your cleaner follows.
              </p>
              <p data-id="element-45" data-mp="22i5o6u68n8rb">
                <strong className="font-bold text-white" data-id="element-46" data-mp="22iowdj636is5">Set it up once</strong> and every future guest gets the same seamless stay — without you answering another midnight message.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-47" data-mp="3ry99n4px18o0">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-48" data-mp="mziatkfne0c">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-49" data-mp="3i3zexgdi3ya4">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-50" data-mp="3mz6cs7k3a8r4">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-51" data-mp="ji6efyfzpcgk">
          <div className="mx-auto max-w-6xl" data-id="element-52" data-mp="5k7zorxrbe0j">
            <div className="mx-auto max-w-2xl text-center" data-id="element-53" data-mp="9m33c335u3hh">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-54" data-mp="9kjn2chi7sfp">
                Fill-in-the-blank templates, ready to use
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-55" data-mp="2udn49xqvfnfu">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-56" data-mp="2hsy6c2gzb03n">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-57" data-mp="2wpxmwftt63a9" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-58" data-mp="2rsxie9clx8l9">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-59" data-mp="2ds4ljhyu21ez">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-60" data-mp="290cy3gaz4bt2">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-61" data-mp="1gds9uopsoftq">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#F1EADB] px-5 py-12 sm:py-16" data-id="element-62" data-mp="zuuzs4h6exza">
          <div className="mx-auto max-w-5xl" data-id="element-63" data-mp="14qabaprpohsj">
            <div className="mx-auto max-w-2xl text-center" data-id="element-64" data-mp="18tgn0cxlf16e">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-65" data-mp="18uruamq8yr29">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-66" data-mp="1ir35f9e3pav4">
                🎁 5 Free Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-67" data-mp="1shg21zz6wuhm">
                Everything you need to hand your property over to a system today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-68" data-mp="1wh6mzn21cge8">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-69" data-mp="21idzfzf4i3r8">
                  <div className="flex items-start gap-4" data-id="element-70" data-mp="3h1bw58dnsoqv">
                    <span className="text-3xl" aria-hidden="true" data-id="element-71" data-mp="3gzr0tgjit1ry">{bonus.emoji}</span>
                    <div data-id="element-72" data-mp="3d00a1ctzulnf">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-73" data-mp="3d1be7drcpvf2">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-74" data-mp="4jx3xbij5517">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-75" data-mp="3vw88nxsi3y9r">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-76" data-mp="ngg59en3don7">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-77" data-mp="31ertatowugxj">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-78" data-mp="2wh9iexrc1rmw" />
            <div data-id="element-79" data-mp="36d30sxrqgfcl">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-80" data-mp="36cjhpdqcjkwz">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-81" data-mp="3fe0x0tvxjikm">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-82" data-mp="3u2dszqv0p1f4">
                Prof. Kyra creates practical, field-tested digital guides designed to solve real everyday problems. Every resource is built to give real tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-83" data-mp="205xfu46a7s3">
                The Hands-Off Hosting System was created for hosts who want a calm, professional operation instead of a phone that never stops buzzing.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-84" data-mp="cjorurn1sztl">
          <div className="mx-auto max-w-4xl text-center" data-id="element-85" data-mp="gksa1kskj7xz">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-86" data-mp="2uf2hjjl9v96">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-87" data-mp="7lx2a49vlpsk">
              After the System
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-88" data-mp="3u0pyh19qs5tn">
              "A great stay is not improvised. It is the result of a system written down once."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-89" data-mp="3kz03xmkkcqrn">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-90" data-mp="1qkl8906oiec7">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-91" data-mp="1lr09h5x9hgby" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-92" data-mp="1grnkaeix3x67">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-93" data-mp="29hw388la2llu">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-94" data-mp="24kvxxzw95mvs">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-95" data-mp="20go6huyetstv" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-96" data-mp="1vi4kjujtgh1i">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-97" data-mp="k3r06zo9ujq8">
              The Hands-Off Hosting System
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-98" data-mp="2vsdyq0yh9nja">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-99" data-mp="30qz2gacxupch">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-100" data-mp="34q7v1bw8sh6g" />
                  <span data-id="element-101" data-mp="39k29o0wzq5yo">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-102" data-mp="3eido6oy8d4n8">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-103" data-mp="3jfdr4qzs9ra4">67 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-104" data-mp="3ogtdqnhm05um">
                27 <span className="align-middle text-2xl" data-id="element-105" data-mp="3sgbxvh1lwcwe">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-106" data-mp="1u28vsikoaoex">
                One-time payment — Yours forever
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-107" data-mp="1y3m0uqfxynks">
              Launch offer · Price goes up soon
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3 w-full" data-id="element-108" data-mp="yru9rb4t6xdg" />
            <p className="mt-4 text-xs font-bold tracking-wide text-slate-500" data-id="element-109" data-mp="1crbrj6jotadb">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-110" data-mp="17s8xsi95qqkn">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-111" data-mp="17tk0eeqv9sa6">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-112" data-mp="k0blyr5pjyjj">
          <div className="mx-auto max-w-3xl" data-id="element-113" data-mp="f55znr8e8hux">
            <div className="text-center" data-id="element-114" data-mp="ts7nydax08t1">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-115" data-mp="tu27bn6jfsv0">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-116" data-mp="1c6m3us68xq0">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-117" data-mp="3sgn64np16qij">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-118" data-mp="27yvwpoj18ijs" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-119" data-mp="2ct05mdbuz0ni">
          <div className="mx-auto max-w-3xl" data-id="element-120" data-mp="1yxxr51sy42z3">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-121" data-mp="232dtwu79iua3" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-122" data-mp="2qqooxgqa1zqg">
              Hand the work to a system. Host hands-off.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-123" data-mp="2hq8pcicnncqv">
              If you set the system up and it does not reduce the guest messages, check-in calls, and turnover chaos in your hosting week, we will refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-124" data-mp="1b9wno7amjcqj">
              Now with the full 6×1 system!
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3" data-id="element-125" data-mp="3cyam3jnpwayx" />
          </div>
        </section>

      </main>

      <footer className="bg-[#0C1729] px-5 py-10 text-center text-white/70" data-id="element-126" data-mp="380sjsc47gtzw">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-127" data-mp="338hcupb1s7xu" />
        <p className="mt-4 text-sm" data-id="element-128" data-mp="2z8qm2llitrtb">© 2026 Prof. Kyra · profkyra.com · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-129" data-mp="2u94b6it60nno">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-130" data-mp="2p8ztipgitmvv">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-131" data-mp="2gaeciyqp6fx7">
            Terms of Service
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-132" data-mp="ei81naxlhmk2">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-133" data-mp="21c37imeufrmx">
          This product is an educational and informational resource for property hosts. It does not constitute legal, tax, or insurance advice.
        </p>
      </footer>

    </div>;
}